import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Palette, Save, Sparkles } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { cn } from '../lib/utils';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import { Input, Label, FieldHint } from './ui/Input';
import Button from './ui/Button';
import Badge from './ui/Badge';

/**
 * STYLE-C (plan v13 T7, 20.10.2026): PROFIL MARKI KLIENTA — tryb (c) stylu.
 * Zapis: `tenants.rules.brand_profile` (JSON). Silnik (worker `brand-style.ts`) buduje z tego blok stylu
 * i dokleja do promptu agenta piszącego treść (Kaufland, Allegro) — zmienia ton i słownictwo, nigdy fakty.
 * Kolory/logo/uwagi do grafik: na razie zapis (A+ v2 je odczyta — GFX-V2).
 */
const TONES = [
  { v: 'rzeczowy, ekspercki, bez marketingu', l: 'Rzeczowy / ekspercki' },
  { v: 'ciepły, przystępny, dla amatorów i majsterkowiczów', l: 'Ciepły / przystępny' },
  { v: 'premium, oszczędny w słowach, podkreśla jakość wykonania', l: 'Premium' },
  { v: 'energiczny, sprzedażowy, podkreśla korzyści i zastosowania', l: 'Sprzedażowy' },
];

const EMPTY = { enabled: true, tone: '', audience: '', values: '', preferred: '', forbidden: '', examples: ['', ''], formatting: '', colors: ['', '', ''], logo_url: '', graphics_notes: '' };

function fromRules(rules) {
  const bp = rules?.brand_profile;
  if (!bp || typeof bp !== 'object') return { ...EMPTY };
  const ex = Array.isArray(bp.examples) ? bp.examples.map(x => String(x || '')) : [];
  const colors = Array.isArray(bp.colors) ? bp.colors.map(x => String(x || '')) : [];
  return { ...EMPTY, ...bp, enabled: bp.enabled !== false, examples: [ex[0] || '', ex[1] || '', ...(ex[2] ? [ex[2]] : [])], colors: [colors[0] || '', colors[1] || '', colors[2] || ''] };
}

function toRules(p) {
  const s = (x) => String(x || '').trim();
  const out = {
    enabled: !!p.enabled, tone: s(p.tone), audience: s(p.audience), values: s(p.values), preferred: s(p.preferred), forbidden: s(p.forbidden),
    examples: (p.examples || []).map(s).filter(Boolean).slice(0, 3),
    formatting: s(p.formatting),
    colors: (p.colors || []).map(s).filter(x => /^#[0-9a-f]{6}$/i.test(x)),
    logo_url: s(p.logo_url), graphics_notes: s(p.graphics_notes),
  };
  const hasText = out.tone || out.audience || out.values || out.preferred || out.forbidden || out.examples.length || out.formatting;
  const hasGfx = out.colors.length || out.logo_url || out.graphics_notes;
  return hasText || hasGfx ? out : null;
}

const SUGGEST_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/brand-style-suggest`;

/**
 * STYLE-B (tryb (b), 21.10): „Zaproponuj z opisów klienta” — edge brand-style-suggest → worker czyta opisy źródłowe klienta
 * (batch_results.original_data z ostatnich zleceń) → propozycja w tenants.rules.brand_profile_suggestion. Tu: odpytywanie
 * co 4 s (do 3 min), podgląd propozycji, „Zastosuj” wypełnia formularz (zapis dopiero po „Zapisz profil”).
 */
function SuggestionBox({ tenant, onApply, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const s = tenant.rules?.brand_profile_suggestion || null;
  const running = s?.status === 'running';

  useEffect(() => {
    if (!running) { setPolling(false); return undefined; }
    setPolling(true);
    let n = 0;
    const t = setInterval(async () => { n += 1; await onRefresh?.(); if (n > 45) { clearInterval(t); setPolling(false); } }, 4000);
    return () => clearInterval(t);
  }, [running, tenant.id]); // eslint-disable-line

  const start = async () => {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(SUGGEST_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` }, body: JSON.stringify({ tenant_id: tenant.id }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      toast.success('Analizuję opisy klienta — propozycja pojawi się tu za ok. minutę.');
      await onRefresh?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-brand-200 dark:border-brand-800 bg-brand-50/40 dark:bg-brand-900/10 p-3 grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-ink-800 dark:text-ink-200 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-brand-600 dark:text-brand-300" /> Propozycja z opisów klienta</div>
        <Button size="sm" variant="secondary" onClick={start} loading={busy || running} disabled={busy || running}>{running ? 'Analizuję…' : s?.status === 'done' ? 'Zaproponuj ponownie' : 'Zaproponuj z opisów klienta'}</Button>
      </div>
      {!s && <p className="text-xs text-ink-500 dark:text-ink-400">Program przeczyta do 20 opisów z plików tego klienta i zaproponuje ton, odbiorcę, zwroty i zakazy. Ty poprawiasz i zapisujesz.</p>}
      {running && <p className="text-xs text-ink-500 dark:text-ink-400">W toku{polling ? '' : ' (odśwież stronę, jeśli trwa dłużej niż 3 min)'}…</p>}
      {s?.status === 'error' && <p className="text-xs text-danger">Nie udało się: {s.error}</p>}
      {s?.status === 'done' && s.profile && (
        <div className="text-xs text-ink-700 dark:text-ink-300 grid gap-1">
          <div><b>Ton:</b> {s.profile.tone}</div>
          <div><b>Odbiorca:</b> {s.profile.audience}</div>
          {s.profile.values && <div><b>Wartości:</b> {s.profile.values}</div>}
          {s.profile.preferred && <div><b>Zwroty:</b> {s.profile.preferred}</div>}
          {s.profile.forbidden && <div><b>Zakazy:</b> {s.profile.forbidden}</div>}
          {s.profile.formatting && <div><b>Układ:</b> {s.profile.formatting}</div>}
          {s.notes && <div className="text-ink-500 dark:text-ink-400"><b>Uwaga:</b> {s.notes} · spójność {Math.round((s.confidence || 0) * 100)}% · {s.source?.descriptions || 0} opisów</div>}
          <div className="flex gap-2 mt-1">
            <Button size="sm" onClick={() => onApply(s.profile)}>Zastosuj do formularza</Button>
            <span className="text-2xs text-ink-400 self-center">potem popraw i „Zapisz profil”</span>
          </div>
        </div>
      )}
    </div>
  );
}

const TA = 'w-full rounded-md text-sm border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 px-3.5 py-2.5 outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10';

export default function BrandProfileCard({ tenant, onSaved, readOnly = false }) {
  const [p, setP] = useState(() => fromRules(tenant.rules));
  const [snap, setSnap] = useState(() => JSON.stringify(fromRules(tenant.rules)));
  const [busy, setBusy] = useState(false);
  useEffect(() => { const f = fromRules(tenant.rules); setP(f); setSnap(JSON.stringify(f)); }, [tenant.id, tenant.rules]);
  const dirty = JSON.stringify(p) !== snap;
  const set = (patch) => setP(prev => ({ ...prev, ...patch }));
  const applySuggestion = (sp) => {
    setP(prev => ({ ...prev, enabled: true, tone: sp.tone || prev.tone, audience: sp.audience || prev.audience, values: sp.values || prev.values, preferred: sp.preferred || prev.preferred, forbidden: sp.forbidden || prev.forbidden, formatting: sp.formatting || prev.formatting,
      examples: (sp.examples && sp.examples.length ? [...sp.examples.slice(0, 3), '', ''].slice(0, Math.max(2, Math.min(3, sp.examples.length))) : prev.examples) }));
    toast.info('Propozycja wstawiona do formularza — sprawdź i zapisz.');
  };
  const profile = toRules(p);
  const active = !!profile && profile.enabled && (profile.tone || profile.audience || profile.values || profile.preferred || profile.forbidden || profile.examples.length || profile.formatting);

  const save = async () => {
    setBusy(true);
    try {
      const nextRules = { ...(tenant.rules || {}) };
      if (profile) nextRules.brand_profile = profile; else delete nextRules.brand_profile;
      const { error } = await supabase.from('tenants').update({ rules: nextRules }).eq('id', tenant.id);
      if (error) throw error;
      setSnap(JSON.stringify(p));
      toast.success(profile ? 'Profil marki zapisany — następne zlecenie pójdzie w tym stylu.' : 'Profil marki wyczyszczony — opisy w stylu standardowym.');
      await onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Palette className="w-4 h-4 text-brand-600 dark:text-brand-300" /> Profil marki</CardTitle>
          <CardDescription>Ton, odbiorca, słownictwo i zakazy klienta — silnik pisze opisy w tym stylu (fakty zawsze z pliku). Bez profilu: styl standardowy.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={active ? 'success' : 'neutral'} size="sm">{active ? 'profil aktywny' : 'styl standardowy'}</Badge>
          {!readOnly && (
            <label className="flex items-center gap-1.5 text-xs text-ink-600 dark:text-ink-300 cursor-pointer">
              <input type="checkbox" checked={!!p.enabled} onChange={e => set({ enabled: e.target.checked })} className="accent-brand-600" /> włączony
            </label>
          )}
        </div>
      </CardHeader>
      <CardBody className="grid gap-4">
        {!readOnly && <SuggestionBox tenant={tenant} onApply={applySuggestion} onRefresh={onSaved} />}
        <div>
          <Label>Ton marki</Label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {TONES.map(t => (
              <button key={t.v} type="button" disabled={readOnly} onClick={() => set({ tone: p.tone === t.v ? '' : t.v })}
                className={cn('px-2.5 py-1 rounded-full border text-xs', p.tone === t.v ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'border-ink-200 text-ink-600 dark:border-ink-700 dark:text-ink-300')}>{t.l}</button>
            ))}
          </div>
          <Input value={p.tone} onChange={e => set({ tone: e.target.value })} disabled={readOnly} placeholder="albo własnymi słowami: np. „konkretnie, jak doświadczony hurtownik do instalatora”" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Do kogo mówimy</Label>
            <Input value={p.audience} onChange={e => set({ audience: e.target.value })} disabled={readOnly} placeholder="np. instalatorzy i hurtownie elektryczne" />
          </div>
          <div>
            <Label>O czym marka zawsze mówi</Label>
            <Input value={p.values} onChange={e => set({ values: e.target.value })} disabled={readOnly} placeholder="np. polska produkcja, serwis w PL, 5 lat gwarancji" />
            <FieldHint>Trafi do opisu tylko tam, gdzie dane produktu to potwierdzają.</FieldHint>
          </div>
          <div>
            <Label>Zwroty pożądane</Label>
            <textarea value={p.preferred} onChange={e => set({ preferred: e.target.value })} disabled={readOnly} rows={3} className={TA} placeholder={'Po przecinku albo w liniach, np.\nsprawdzony w praktyce\nsolidne wykonanie'} />
          </div>
          <div>
            <Label>Zakazane słowa i obietnice</Label>
            <textarea value={p.forbidden} onChange={e => set({ forbidden: e.target.value })} disabled={readOnly} rows={3} className={TA} placeholder={'np.\nnajlepszy, rewolucyjny\ngwarancja dożywotnia\nnajniższa cena'} />
            <FieldHint>Twarde — silnik ich nie użyje w żadnym języku.</FieldHint>
          </div>
        </div>
        <div>
          <Label>Przykładowe opisy klienta (1–3, styl — nie fakty)</Label>
          <div className="grid gap-2">
            {p.examples.map((ex, i) => (
              <textarea key={i} value={ex} onChange={e => set({ examples: p.examples.map((x, j) => j === i ? e.target.value : x) })} disabled={readOnly} rows={3} className={TA} placeholder={`Przykład ${i + 1}: wklej fragment opisu, który klient uważa za „swój” (do 700 znaków)`} maxLength={1500} />
            ))}
            {p.examples.length < 3 && !readOnly && <button type="button" onClick={() => set({ examples: [...p.examples, ''] })} className="text-xs text-brand-700 dark:text-brand-300 hover:underline text-left">+ dodaj trzeci przykład</button>}
          </div>
        </div>
        <div>
          <Label>Układ opisu</Label>
          <Input value={p.formatting} onChange={e => set({ formatting: e.target.value })} disabled={readOnly} placeholder="np. krótkie akapity, zawsze lista parametrów na końcu, bez tabel" />
        </div>
        <div className="rounded-xl border border-dashed border-ink-200 dark:border-ink-700 p-3 grid gap-3 sm:grid-cols-[auto_1fr] items-start">
          <div>
            <Label>Kolory marki</Label>
            <div className="flex gap-2">
              {p.colors.map((c, i) => (
                <div key={i} className="flex items-center gap-1">
                  <input type="color" value={/^#[0-9a-f]{6}$/i.test(c) ? c : '#ffffff'} onChange={e => set({ colors: p.colors.map((x, j) => j === i ? e.target.value : x) })} disabled={readOnly} className="w-8 h-8 rounded border border-ink-200 dark:border-ink-700 bg-transparent" aria-label={`Kolor ${i + 1}`} />
                  <Input value={c} onChange={e => set({ colors: p.colors.map((x, j) => j === i ? e.target.value : x) })} disabled={readOnly} placeholder="#1a2b3c" className="w-24 font-mono text-xs" />
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-2">
            <div>
              <Label>Logo (adres pliku)</Label>
              <Input value={p.logo_url} onChange={e => set({ logo_url: e.target.value })} disabled={readOnly} placeholder="https://…/logo.png" />
            </div>
            <div>
              <Label>Uwagi do grafik</Label>
              <Input value={p.graphics_notes} onChange={e => set({ graphics_notes: e.target.value })} disabled={readOnly} placeholder="np. jasne tło, produkt na warsztacie, bez ludzi" />
              <FieldHint>Kolory, logo i uwagi czytają grafiki A+ w wersji 2 (w planie) — dziś tylko zapis.</FieldHint>
            </div>
          </div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-3">
            <Button onClick={save} loading={busy} disabled={!dirty} leftIcon={<Save className="w-4 h-4" />}>Zapisz profil</Button>
            {dirty && <span className="text-xs text-warning-dark dark:text-warning font-medium">niezapisane zmiany</span>}
            <span className="text-2xs text-ink-400 dark:text-ink-500">Działa od następnego zlecenia (Kaufland, Allegro). Test: 10 produktów, potem ocena w Gotowości.</span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
